import { useCallback, useEffect, useRef, useState, type ReactNode, type ComponentProps } from 'react';
import {
  AcBadge,
  AcButton,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionShell,
} from '@/dashboard/ui';
import { DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ConsultationDialog as Dialog, useConsultationWithheld } from './ConsultationPresentation';
import {
  EvidenceCaptureControl,
  type CapturedEvidencePhoto,
} from '@/dashboard/ui/EvidenceCapture';
import {
  attachCaseSheetEvidenceApi,
  fetchCaseSheetEvidence,
  fetchEvidenceFullQuality,
  fetchEvidenceRendition,
  voidClinicalEvidenceApi,
  type CaseSheetEvidenceList,
  type AttachEvidenceInput,
  type ClinicalEvidenceItem,
} from '@/dashboard/api';
import {
  activeItemsOfClass,
  evidenceClassLabel,
  evidenceVoidReasonLabel,
  EVIDENCE_VOID_REASON_LABELS,
  owedKindsSummary,
  voidedItems,
} from '@/dashboard/doctor/clinical-evidence-view';
import ClinicalPdfPreview from './ClinicalPdfPreview';
import { authBearer, PortalApiError } from '@/lib/portal-api';
import './clinical-evidence.css';

/**
 * DOCTOR-CLINICAL-EVIDENCE-TIER2-V1 -- the consult CLINICAL EVIDENCE panel
 * (the merged Doctor OS design #412 s.7.5/7.8) + the S-contract capture
 * dialog both the panel and the Doctor Inbox open.
 *
 * PAPER FIRST (tenant evidence E1/E2; founder ruling R-PAPER-BRIDGE): the
 * panel LEADS with Attach case-sheet pages; intraoral photos / X-rays /
 * consent scans follow; each item carries its optional per-photo remark.
 * Thumbnails render the compressed delivery rendition; TAP opens the
 * FULL-QUALITY original through the audited endpoint (D2). Voided items
 * render demoted with their reason -- corrections never disappear.
 *
 * RENDERS ONLY behind me.clinicalEvidenceEnabled (flag B4; the workspace
 * mounts this panel only when the signal is present -- flag-OFF = zero
 * surfaces). All storage rides THE ONE gateway server-side; this panel
 * performs no validation beyond the shared control's beside-the-field
 * mirrors.
 *
 * MATERIALIZE-ON-ATTACH (the readiness report's paper-first contract
 * defect, fixed): a visit with no case sheet yet materializes one through
 * THE ONE existing draft writer (the page's ensureSheet -> persistDraft ->
 * POST /api/case-sheets) BEFORE the first attach -- an evidence attachment
 * is a sheet-materializing act exactly as a keystroke is, which is what
 * makes finalize-from-evidence (DR-3/B3) reachable with zero typed prose.
 */

// Application-specific evidence content inside the installed accessible Dialog.
// Its modal/focus/dismissal behavior is reused, never implemented here.
export function EvidenceDialog({ title, eyebrow, onClose, children, footer, testId, returnFocus, open = true, onOpenChangeComplete,
  closeDisabled = false, closeOnEscape = true, closeOnScrim = true, presentation,
}: { title: string; eyebrow?: string; onClose: () => void; children: ReactNode;
  onOpenChangeComplete?: ComponentProps<typeof Dialog>['onOpenChangeComplete'];
  presentation?: 'v14g-consent'; footer?: ReactNode; testId?: string; returnFocus?: ComponentProps<typeof DialogContent>['finalFocus']; open?: boolean; closeDisabled?: boolean; closeOnEscape?: boolean; closeOnScrim?: boolean;
}): JSX.Element {
  const origin = useRef<HTMLElement | null>(typeof document === 'undefined' ? null : document.activeElement as HTMLElement | null);
  return <Dialog open={open} onOpenChangeComplete={onOpenChangeComplete} onOpenChange={(open, details) => {
    if (!open && (closeDisabled || (details.reason === 'escape-key' && !closeOnEscape) || (details.reason === 'outside-press' && !closeOnScrim))) { details.cancel(); return; }
    if (!open) onClose();
  }}><DialogContent className={`evidence-clinical-dialog${presentation ? " consult-v14g__consent-dialog" : ""}`} showCloseButton={false} finalFocus={returnFocus ?? origin} data-testid={testId}>
    <div className="evidence-clinical-dialog__head"><div>
      {eyebrow ? <DialogDescription className="evidence-clinical-dialog__eyebrow">{eyebrow}</DialogDescription> : null}
      <DialogTitle>{title}</DialogTitle>
    </div>{!presentation ? <AcButton size="sm" variant="ghost" disabled={closeDisabled} onClick={onClose}>Close</AcButton> : null}</div>
    <div className="evidence-clinical-dialog__body">{children}</div>
    {footer || presentation ? <div className="evidence-clinical-dialog__footer">{footer}{presentation ? <AcButton variant="primary" disabled={closeDisabled} onClick={onClose}>Close</AcButton> : null}</div> : null}
  </DialogContent></Dialog>;
}

// Native browser playback inside the existing evidence dialog. No remote URL,
// transcoding, autoplay, signature claim or alternate storage path.
function EvidenceVideo({ src, label, staged = false }: { src: string; label: string; staged?: boolean }): JSX.Element {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return <div>
    <video key={src} className={staged ? 'evidence-dialog__preview' : 'evidence-full__img'}
      src={src} controls playsInline preload="metadata" aria-label={label}
      onError={() => setFailed(true)} data-testid="clinical-video-preview" />
    {failed ? <p className="evidence-dialog__error" role="alert">This browser could not play this video.{staged ? ' Choose a playable MP4 or WebM file.' : ' The saved original is unchanged.'}</p> : null}
  </div>;
}

// --- The capture dialog (S-contract: title / content / footer; one
// primary right, quiet secondary; Esc + scrim safe via DrawerShell) -------

export interface EvidenceCaptureDialogProps {
  /** Approved direct-picker flow, using this same preview and store. */
  readonly initialCapture?: { readonly photo: CapturedEvidencePhoto; readonly toothRefs: readonly string[]; readonly bearer: string };
  readonly initialClass?: string;
  readonly photoTiming?: boolean;
  readonly patientLabel?: string;
  readonly returnFocus?: ComponentProps<typeof DialogContent>['finalFocus'];
  /** 2-4 word title (S-2). */
  readonly title: string;
  readonly caseSheetId: number;
  /** Fixed class = the multi-shot flow (case-sheet pages / an inbox item's
   *  owed kind); null = the class picker renders. */
  readonly fixedClass: string | null;
  /** Org capture config. ABSENT = the dialog resolves it itself from the
   *  visit's evidence read (the inbox / just-materialized-sheet path) --
   *  org config always wins; nothing hard-codes the vocabulary. */
  readonly config?: CaseSheetEvidenceList['config'];
  /** Fired after EVERY successful attach (refetch signal). */
  readonly onAttached: (item?: ClinicalEvidenceItem) => void | Promise<void>;
  /** Optional application-specific association over the SAME evidence writer. */
  readonly attach?: (input: AttachEvidenceInput) => Promise<void>;
  readonly retainPending?: boolean;
  readonly onClose: () => void;
}

/** Bind the selected patient/session before asynchronous file decoding. Retake
 * changes only the original bytes in the dialog, never this capture target. */
export function snapshotDirectEvidenceTarget(toothRefs: readonly string[]) {
  return { toothRefs: Object.freeze([...toothRefs]), bearer: authBearer().catch(() => null) };
}
export async function resolveDirectEvidenceTarget(target: ReturnType<typeof snapshotDirectEvidenceTarget>, stillCurrent: () => boolean) {
  const bearer = await target.bearer;
  if (!bearer || !stillCurrent()) throw Error('The visit or session changed. Reopen it before uploading.');
  const currentBearer = await authBearer();
  if (!stillCurrent() || currentBearer !== bearer) throw Error('The visit or session changed. Reopen it before uploading.');
  return { toothRefs: target.toothRefs, bearer };
}

type DirectCaptureAttempt = { input: AttachEvidenceInput; caseSheetId: number; bearer: string; uncertain: boolean };
/** Direct captures retain one immutable request until the existing owner verifies
 * its scoped list. Shared consent/report adapters keep their own retry owners. */
export async function attachDirectEvidenceCapture(
  attempt: { current: DirectCaptureAttempt | null }, caseSheetId: number, bearer: string,
  input: AttachEvidenceInput, stillCurrent: () => boolean,
  confirm: (item: ClinicalEvidenceItem) => void | Promise<void>,
): Promise<ClinicalEvidenceItem> {
  if (!stillCurrent() || await authBearer() !== bearer) throw Error('The session changed. Reopen this visit.');
  if (attempt.current && (attempt.current.caseSheetId !== caseSheetId || attempt.current.bearer !== bearer)) throw Error('The visit or session changed. Retry this upload in its original visit.');
  const current = attempt.current ?? { input: { ...input, ...(input.toothRefs ? { toothRefs: [...input.toothRefs] } : {}), requestId: crypto.randomUUID() }, caseSheetId, bearer, uncertain: false };
  attempt.current = current;
  let item: ClinicalEvidenceItem;
  try { item = (await attachCaseSheetEvidenceApi(caseSheetId, current.input, bearer)).evidence; }
  catch (error) {
    if (!current.uncertain && error instanceof PortalApiError && error.status >= 400 && error.status < 500) attempt.current = null;
    else current.uncertain = true;
    throw error;
  }
  current.uncertain = true;
  if (!stillCurrent() || await authBearer() !== bearer) throw Error('The session changed. Check the upload in its original visit.');
  if (item.id !== current.input.requestId || item.caseSheetId !== caseSheetId) throw Error('The saved upload could not be confirmed. Retry the same capture.');
  await confirm(item);
  // The owner may close/unmount after successful readback. Clear only now.
  attempt.current = null;
  return item;
}

type CaptureState =
  | { kind: 'idle' }
  | { kind: 'staged'; photo: CapturedEvidencePhoto }
  | { kind: 'uploading'; photo: CapturedEvidencePhoto }
  | { kind: 'failed'; photo: CapturedEvidencePhoto; message: string };

export function EvidenceCaptureDialog(props: EvidenceCaptureDialogProps): JSX.Element {
  return <EvidenceCaptureDialogBody key={props.caseSheetId} {...props} />;
}

function EvidenceCaptureDialogBody(props: EvidenceCaptureDialogProps): JSX.Element {
  const withheld = useConsultationWithheld();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [capture, setCapture] = useState<CaptureState>(props.initialCapture ? { kind: 'staged', photo: props.initialCapture.photo } : { kind: 'idle' });
  const writing = useRef(false);
  const withheldRef = useRef(withheld); withheldRef.current = withheld;
  const [photoPhase, setPhotoPhase] = useState<'before' | 'after' | null>(null);
  const attempt = useRef<DirectCaptureAttempt | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [readingFile, setReadingFile] = useState(false);
  const [evidenceClass, setEvidenceClass] = useState<string>(
    props.fixedClass ?? props.initialClass ?? 'case_sheet_page',
  );
  const [remark, setRemark] = useState('');
  const [attachedCount, setAttachedCount] = useState(0);
  // Config resolution: caller-provided, else fetched from the visit's own
  // evidence read (org config always wins; no hard-coded vocabulary).
  const [fetchedConfig, setFetchedConfig] = useState<
    CaseSheetEvidenceList['config'] | 'loading' | 'error'
  >(props.config === undefined ? 'loading' : props.config);
  useEffect(() => {
    if (props.config !== undefined || withheld) return;
    let cancelled = false;
    fetchCaseSheetEvidence(props.caseSheetId)
      .then((data) => {
        if (cancelled) return;
        setFetchedConfig(data.config);
        setEvidenceClass(
          (prev) => props.fixedClass ?? (data.config.kinds.includes(prev) ? prev : data.config.kinds[0] ?? prev),
        );
      })
      .catch(() => {
        if (!cancelled) setFetchedConfig('error');
      });
    return () => {
      cancelled = true;
    };
  }, [props.caseSheetId, props.config, props.fixedClass, withheld]);
  const config =
    props.config !== undefined
      ? props.config
      : fetchedConfig === 'loading' || fetchedConfig === 'error'
        ? null
        : fetchedConfig;

  const busy = capture.kind === 'uploading';
  const retainPending = props.retainPending || attempt.current !== null;

  if (config === null) {
    return (
      <EvidenceDialog
        eyebrow="Clinical evidence"
        title={props.title}
        returnFocus={props.returnFocus}
        onClose={props.onClose}
        testId="evidence-capture-dialog"
      >
        {fetchedConfig === 'error' ? (
          <ErrorState
            title="Capture is not available."
            detail="The capture settings could not load. Close and try again."
            action={
              <AcButton size="sm" variant="secondary" onClick={props.onClose}>
                Close
              </AcButton>
            }
          />
        ) : (
          <LoadingState label="Preparing capture..." lines={2} />
        )}
      </EvidenceDialog>
    );
  }

  const onAttach = async () => {
    if (writing.current || readingFile || (capture.kind !== 'staged' && capture.kind !== 'failed')) return;
    writing.current = true;
    const photo = capture.photo;
    setCapture({ kind: 'uploading', photo });
    try {
      const input: AttachEvidenceInput = {
        evidenceClass,
        imageBase64: photo.base64,
        imageMimeType: photo.mimeType,
        ...(photo.renditionBase64 !== null
          ? { renditionBase64: photo.renditionBase64 }
          : {}),
        ...(remark.trim() !== '' ? { remark: remark.trim() } : {}),
      };
      let attached: ClinicalEvidenceItem | undefined;
      if (props.initialCapture) {
        attached = await attachDirectEvidenceCapture(attempt, props.caseSheetId, props.initialCapture.bearer,
          { ...input, toothRefs: ['xray', 'intraoral_photo'].includes(evidenceClass) ? [...props.initialCapture.toothRefs] : [],
            photoPhase: props.photoTiming && evidenceClass === 'intraoral_photo' && photo.mimeType.startsWith('image/') ? photoPhase : null },
          () => mounted.current && !withheldRef.current, props.onAttached);
      } else if (props.attach) await props.attach(input);
      else attached = (await attachCaseSheetEvidenceApi(props.caseSheetId, input)).evidence;
      if (!mounted.current) return;
      if (!props.initialCapture) await props.onAttached(attached);
      if (!mounted.current) return;
      attempt.current = null;
      // Multi-shot: stay open, ready for the next page/photo; the item
      // never clears client-side without the server's success.
      setAttachedCount((n) => n + 1);
      setRemark('');
      setCapture({ kind: 'idle' });

    } catch (err) {
      if (!mounted.current) return;
      setCapture({
        kind: 'failed',
        photo,
        message:
          err instanceof Error && err.message !== ''
            ? err.message
            : props.initialCapture ? 'The upload could not be confirmed. Retry the same capture.' : 'The file could not be stored. It is NOT attached yet - try again.',
      });
    } finally { writing.current = false; }
  };

  return (
    <EvidenceDialog
      eyebrow="Clinical evidence"
      returnFocus={props.returnFocus}
      title={props.title}
      onClose={props.onClose}
      closeOnScrim={!busy && !retainPending}
      closeOnEscape={!busy && !retainPending}
      closeDisabled={busy || retainPending}
      testId="evidence-capture-dialog"
      footer={
        <div className="evidence-dialog__footer">
          <AcButton
            size="sm"
            variant="ghost"
            onClick={props.onClose}
            disabled={busy || retainPending}
            data-testid="evidence-capture-done"
          >
            {attachedCount > 0 ? 'Done' : 'Cancel'}
          </AcButton>
          <AcButton
            size="sm"
            variant="primary"
            onClick={() => void onAttach()}
            disabled={
              busy || readingFile || (capture.kind !== 'staged' && capture.kind !== 'failed')
            }
            data-testid="evidence-capture-attach"
          >
            {busy ? 'Attaching...' : 'Attach'}
          </AcButton>
        </div>
      }
    >
      <div className="evidence-dialog">
        {props.patientLabel ? <p className="evidence-dialog__patient">For {props.patientLabel}</p> : null}
        {props.initialCapture && ['xray', 'intraoral_photo'].includes(evidenceClass) && props.initialCapture.toothRefs.length > 0 ? <p data-testid="evidence-selected-teeth">Tooth {props.initialCapture.toothRefs.join(', ')}</p> : null}
        {props.fixedClass === null ? (
          <div className="evidence-dialog__kinds" data-testid="evidence-kind-picker">
            <span className="evidence-dialog__label">Kind</span>
            <div className="evidence-dialog__kind-chips">
              {config.kinds.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className={
                    kind === evidenceClass
                      ? 'evidence-kind-chip evidence-kind-chip--on'
                      : 'evidence-kind-chip'
                  }
                  aria-pressed={kind === evidenceClass}
                  onClick={() => setEvidenceClass(kind)}
                  disabled={busy || retainPending}
                  data-testid={`evidence-kind-${kind}`}
                >
                  {evidenceClassLabel(kind)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="evidence-dialog__fixed-kind">
            <span className="evidence-dialog__label">Kind</span>
            <AcBadge tone="pool">{evidenceClassLabel(evidenceClass)}</AcBadge>
          </p>
        )}

        {attachedCount > 0 ? (
          <p className="evidence-dialog__count" data-testid="evidence-attached-count">
            {attachedCount === 1
              ? '1 item attached this session.'
              : `${attachedCount} items attached this session.`}
          </p>
        ) : null}

        {capture.kind === 'idle' ? (
          <div className="evidence-dialog__capture">
            <EvidenceCaptureControl
              subject={evidenceClass}
              captureSources={config.captureSources}
              maxBytes={config.maxBytes}
              allowedTypes={props.initialCapture ? config.allowedMime.filter(type => type.startsWith('image/') || (props.photoTiming && type === 'application/pdf')) : config.allowedMime}
              renditionMaxBytes={config.renditionMaxBytes}
              onReadStateChange={setReadingFile}
              label={config.allowedMime.some(type => type === 'application/pdf' || type.startsWith('video/')) ? 'Take photo or choose file' : 'Take photo'}
              onCaptured={(photo) => {
                setPickError(null);
                setCapture({ kind: 'staged', photo });
              }}
              onError={(message) => setPickError(message)}
              testId="evidence-take-photo"
            />
            {pickError !== null ? (
              <p className="evidence-dialog__error" role="alert">
                {pickError}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="evidence-dialog__staged">
            {capture.photo.mimeType === 'application/pdf'
              ? <ClinicalPdfPreview base64={capture.photo.base64} label="Selected PDF" filename={capture.photo.fileName} staged />
              : capture.photo.mimeType.startsWith('video/')
                ? <EvidenceVideo src={capture.photo.previewDataUrl} label="Selected video" staged />
              : <img className="evidence-dialog__preview" alt={evidenceClassLabel(evidenceClass)} src={capture.photo.previewDataUrl} />}
            <div className="evidence-dialog__staged-meta">
              <span>
                {Math.max(1, Math.round(capture.photo.byteSize / 1024))} KB
              </span>
              <EvidenceCaptureControl
                subject={evidenceClass}
                captureSources={config.captureSources}
                maxBytes={config.maxBytes}
                allowedTypes={props.initialCapture ? config.allowedMime.filter(type => type.startsWith('image/') || (props.photoTiming && type === 'application/pdf')) : config.allowedMime}
                renditionMaxBytes={config.renditionMaxBytes}
              onReadStateChange={setReadingFile}
                label={capture.photo.mimeType === 'application/pdf' ? 'Choose another file' : 'Retake'}
                variant="secondary"
                busy={busy || retainPending}
                onCaptured={(photo) => {
                  setPickError(null);
                  setCapture({ kind: 'staged', photo });
                }}
                onError={(message) => setPickError(message)}
                testId="evidence-retake"
              />
            </div>
            {props.photoTiming && evidenceClass === 'intraoral_photo' && capture.photo.mimeType.startsWith('image/') ? <div className="evidence-dialog__kinds" role="group" aria-label="Before or after treatment">
              <span className="evidence-dialog__label">This photo is</span>
              {([['before', 'Before treatment'], ['after', 'After treatment'], [null, 'Other']] as const).map(([value, label]) => <AcButton key={label} variant={photoPhase === value ? 'primary' : 'secondary'} aria-pressed={photoPhase === value} disabled={busy || retainPending} onClick={() => setPhotoPhase(value)}>{label}</AcButton>)}
            </div> : null}
            <label className="evidence-dialog__remark">
              <span className="evidence-dialog__label">Remark (optional)</span>
              <input
                type="text"
                value={remark}
                maxLength={500}
                disabled={busy || retainPending}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="e.g. Page 1 of 2, lower left"
                data-testid="evidence-remark-input"
              />
            </label>
            {capture.kind === 'uploading' ? (
              <p
                className="evidence-dialog__pending"
                role="status"
                data-testid="evidence-upload-pending"
              >
                {'Storing the file... Do not close this dialog.'}
              </p>
            ) : null}
            {capture.kind === 'failed' ? (
              <p
                className="evidence-dialog__error"
                role="alert"
                data-testid="evidence-upload-failed"
              >
                {capture.message}
              </p>
            ) : null}
            {pickError !== null ? (
              <p className="evidence-dialog__error" role="alert">
                {pickError}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </EvidenceDialog>
  );
}

// --- Thumbnail (rendition; tap = audited full-quality open) ---------------

function EvidenceThumb(props: {
  readonly item: ClinicalEvidenceItem;
  readonly clinicId: number;
  readonly onOpenFull: (item: ClinicalEvidenceItem) => void;
}): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current || !props.item.hasRendition) return;
    requested.current = true;
    fetchEvidenceRendition(props.item.id, props.clinicId)
      .then((bytes) =>
        setSrc(`data:${bytes.mimeType};base64,${bytes.dataBase64}`),
      )
      .catch(() => setFailed(true));
  }, [props.item.id, props.item.hasRendition, props.clinicId]);

  return (
    <button
      type="button"
      className="evidence-thumb"
      onClick={() => props.onOpenFull(props.item)}
      data-testid={`evidence-thumb-${props.item.id}`}
      title="Open full quality"
    >
      {src !== null ? (
        <img
          className="evidence-thumb__img"
          alt={evidenceClassLabel(props.item.evidenceClass)}
          src={src}
        />
      ) : (
        <span className="evidence-thumb__label">
          {failed || !props.item.hasRendition
            ? evidenceClassLabel(props.item.evidenceClass)
            : 'Loading...'}
        </span>
      )}
      {props.item.toothRefs?.length ? <span className="evidence-thumb__remark">Tooth {props.item.toothRefs.join(', ')}</span> : null}
      {props.item.photoPhase ? <span className="evidence-thumb__remark">{props.item.photoPhase === 'before' ? 'Before' : 'After'} treatment</span> : null}
      {props.item.remark !== null ? (
        <span className="evidence-thumb__remark">{props.item.remark}</span>
      ) : null}
    </button>
  );
}

// --- Full-quality viewer (audited open) -----------------------------------

export function EvidenceFullView(props: {
  readonly item: ClinicalEvidenceItem;
  readonly clinicId: number;
  readonly onClose: () => void;
  readonly returnFocus?: ComponentProps<typeof EvidenceDialog>['returnFocus'];
  readonly open?: boolean;
  readonly onOpenChangeComplete?: ComponentProps<typeof EvidenceDialog>['onOpenChangeComplete'];
  readonly presentation?: 'v14g-consent';
  readonly title?: string;
}): JSX.Element {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; src: string; byteSize: number; base64: string; mimeType: string }
  >({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (props.open === false) { setState({ kind: 'loading' }); return; }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchEvidenceFullQuality(props.item.id, props.clinicId)
      .then((bytes) => {
        if (cancelled) return;
        setState({
          kind: 'ready',
          src: `data:${bytes.mimeType};base64,${bytes.dataBase64}`,
          byteSize: bytes.byteSize, base64: bytes.dataBase64, mimeType: bytes.mimeType,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message:
            err instanceof Error ? err.message : 'The photo could not load.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [props.item.id, props.clinicId, props.open, attempt]);

  return (
    <EvidenceDialog
      eyebrow={evidenceClassLabel(props.item.evidenceClass)}
      title={props.title ?? "Full quality"}
      presentation={props.presentation}
      onClose={props.onClose}
      returnFocus={props.returnFocus}
      open={props.open}
      onOpenChangeComplete={props.onOpenChangeComplete}
      testId="evidence-full-view"
    >
      {state.kind === 'loading' ? (
        <LoadingState label="Loading full quality..." lines={3} />
      ) : state.kind === 'error' ? (
        <ErrorState
          title="The file could not load."
          detail={state.message}
          action={
            <AcButton size="sm" variant="secondary" onClick={() => setAttempt(value => value + 1)}>
              Retry
            </AcButton>
          }
        />
      ) : (
        <div className="evidence-full">
          {state.mimeType === 'application/pdf'
            ? <ClinicalPdfPreview base64={state.base64} label={evidenceClassLabel(props.item.evidenceClass)} />
            : state.mimeType.startsWith('video/')
              ? <EvidenceVideo src={state.src} label="Saved video" />
            : <img className="evidence-full__img" alt={evidenceClassLabel(props.item.evidenceClass)} src={state.src} />}
          <div className="evidence-full__meta">
            <span>{evidenceClassLabel(props.item.evidenceClass)}</span>
            <span>{Math.max(1, Math.round(state.byteSize / 1024))} KB</span>
            {props.item.toothRefs?.length ? <span>Tooth {props.item.toothRefs.join(', ')}</span> : null}
            {props.item.photoPhase ? <span>{props.item.photoPhase === 'before' ? 'Before' : 'After'} treatment</span> : null}
            {props.item.remark !== null ? (
              <span data-testid="evidence-full-remark">{props.item.remark}</span>
            ) : null}
          </div>
        </div>
      )}
    </EvidenceDialog>
  );
}

// --- Void dialog (reason-coded; the ONE correction path) -------------------

function EvidenceVoidDialog(props: {
  readonly item: ClinicalEvidenceItem;
  readonly clinicId: number;
  readonly onVoided: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onVoid = async () => {
    if (reason === null) return;
    setBusy(true);
    setError(null);
    try {
      await voidClinicalEvidenceApi(props.item.id, {
        clinicId: props.clinicId,
        voidReason: reason,
      });
      props.onVoided();
      props.onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'The void could not be recorded.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <EvidenceDialog
      eyebrow={evidenceClassLabel(props.item.evidenceClass)}
      title="Void evidence"
      onClose={props.onClose}
      closeOnScrim={!busy}
      closeOnEscape={!busy}
      closeDisabled={busy}
      testId="evidence-void-dialog"
      footer={
        <div className="evidence-dialog__footer">
          <AcButton size="sm" variant="ghost" onClick={props.onClose} disabled={busy}>
            Cancel
          </AcButton>
          <AcButton
            size="sm"
            variant="danger"
            onClick={() => void onVoid()}
            disabled={busy || reason === null}
            data-testid="evidence-void-confirm"
          >
            {busy ? 'Voiding...' : 'Void'}
          </AcButton>
        </div>
      }
    >
      <div className="evidence-dialog">
        <p className="evidence-dialog__body">
          The photo stays on record, demoted with its reason - it never
          disappears. Re-capture a corrected photo after voiding.
        </p>
        <div className="evidence-dialog__kind-chips" data-testid="evidence-void-reasons">
          {EVIDENCE_VOID_REASON_LABELS.map((r) => (
            <button
              key={r.token}
              type="button"
              className={
                r.token === reason
                  ? 'evidence-kind-chip evidence-kind-chip--on'
                  : 'evidence-kind-chip'
              }
              onClick={() => setReason(r.token)}
              disabled={busy}
              data-testid={`evidence-void-reason-${r.token}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {error !== null ? (
          <p className="evidence-dialog__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </EvidenceDialog>
  );
}

// --- The panel --------------------------------------------------------------

export interface ClinicalEvidenceSource {
  readonly caseSheetId: number;
  readonly list: CaseSheetEvidenceList | null;
  readonly refresh: () => Promise<CaseSheetEvidenceList | null>;
}

export interface ClinicalEvidencePanelProps {
  readonly selectedToothRefs?: readonly string[];
  readonly patientLabel?: string;
  readonly captureConfig?: CaseSheetEvidenceList['config'] | null;
  /** Investigation reports reuse this owner's scoped metadata read. */
  readonly onEvidenceSource?: (source: ClinicalEvidenceSource | null) => void;
  readonly embedded?: boolean;
  readonly presentation?: 'v14g';
  readonly clinicId: number;
  /** The live sheet identity (null = no sheet materialized yet). */
  readonly sheet: { readonly id: number; readonly status: string } | null;
  /** Materialize-on-attach: resolves the sheet id, creating the draft
   *  through THE ONE existing writer when absent. Null = create failed
   *  (the caller surfaced the error). */
  readonly ensureSheet: () => Promise<number | null>;
  /** DOCTOR-COCKPIT-VISUAL-CONFORM-V1 round 2 (coverage C28): mirrors the
   *  ACTIVE imaging count (xray + intraoral_photo classes) up from this
   *  panel's ONE list read so the consent card's index line composes with
   *  zero second fetch (the one-fetch-two-renders idiom). Fired with null
   *  while the read is unsettled / failed / sheetless -- the consumer then
   *  renders ABSENCE, never a zero it invented. */
  readonly onImagingCount?: (count: number | null) => void;
  /** Another approved entry point into this same capture owner; no second writer. */
  readonly onPaperCapture?: (entry: { open: () => void; busy: boolean; error: string | null } | null) => void;
  /** DOCTOR-CONSULT-CORRECTIVE-V1 (BUILD 1, L-05): mirrors the SERVED
   *  owed-evidence kinds up from this panel's ONE list read, so the close
   *  confirm 2,600 lines above can state what the visit still owes with ZERO
   *  second fetch (the same one-fetch-two-renders idiom as onImagingCount).
   *  Fired with null while the read is unsettled / failed / sheetless -- the
   *  consumer then renders ABSENCE, never a "nothing owed" it invented. */
  readonly onOwedKinds?: (kinds: readonly string[] | null) => void;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; list: CaseSheetEvidenceList };

type DialogState =
  | { kind: 'none' }
  | { kind: 'capture'; caseSheetId: number; fixedClass: string | null; title: string; initialCapture?: EvidenceCaptureDialogProps['initialCapture']; initialClass?: string; photoTiming?: boolean }
  | { kind: 'full'; item: ClinicalEvidenceItem }
  | { kind: 'void'; item: ClinicalEvidenceItem };

export default function ClinicalEvidencePanel(
  props: ClinicalEvidencePanelProps,
): JSX.Element {
  const withheld = useConsultationWithheld();
  const presentation = useRef({ withheld, version: 0 });
  if (presentation.current.withheld !== withheld) presentation.current = { withheld, version: presentation.current.version + 1 };
  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [ensureBusy, setEnsureBusy] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);
  const selectedCapture = useRef<{ target: ReturnType<typeof snapshotDirectEvidenceTarget>; version: number } | null>(null);
  const captureOrigin = useRef<HTMLElement | null>(null);
  const [readingCapture, setReadingCapture] = useState(false);
  const sheetId = props.sheet?.id ?? null;
  const mounted = useRef(true);
  const readGeneration = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; readGeneration.current++; }; }, []);
  // The C28 mirror rides a ref (the ConsentPanel onWireChange idiom) so an
  // inline caller arrow never re-arms the fetch effect.
  const onImagingCountRef = useRef(props.onImagingCount);
  onImagingCountRef.current = props.onImagingCount;
  const onOwedKindsRef = useRef(props.onOwedKinds);
  onOwedKindsRef.current = props.onOwedKinds;

  const refetch = useCallback(() => {
    if (sheetId === null || presentation.current.withheld) return Promise.resolve(null);
    const generation = ++readGeneration.current;
    return fetchCaseSheetEvidence(sheetId)
      .then((data) => {
        if (!mounted.current || generation !== readGeneration.current) return null;
        setList({ kind: 'ready', list: data });
        onImagingCountRef.current?.(
          activeItemsOfClass(data.items, 'xray').length +
            activeItemsOfClass(data.items, 'intraoral_photo').length,
        );
        onOwedKindsRef.current?.(data.owedKinds);
        return data;
      })
      .catch((err) => {
        if (!mounted.current || generation !== readGeneration.current) return null;
        setList({
          kind: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'Evidence could not be loaded.',
        });
        onImagingCountRef.current?.(null);
        onOwedKindsRef.current?.(null);
        return null;
      });
  }, [sheetId, withheld]);

  useEffect(() => {
    readGeneration.current++;
    onImagingCountRef.current?.(null);
    onOwedKindsRef.current?.(null);
    if (withheld) {
      setList({ kind: 'loading' });
      // Read-only originals close; capture/void owners retain pending state.
      setDialog(current => current.kind === 'full' ? { kind: 'none' } : current);
      return;
    }
    if (sheetId === null) {
      // No sheet -> no read -> the mirrored facts are honestly unknown.
      onImagingCountRef.current?.(null);
      onOwedKindsRef.current?.(null);
      return;
    }
    setList({ kind: 'loading' });
    refetch();
    return () => { readGeneration.current++; };
  }, [sheetId, refetch, withheld]);

  const evidenceSourceRef = useRef(props.onEvidenceSource);
  evidenceSourceRef.current = props.onEvidenceSource;
  useEffect(() => {
    evidenceSourceRef.current?.(sheetId === null || withheld ? null : {caseSheetId: sheetId, list: list.kind === 'ready' ? list.list : null, refresh: refetch});
  }, [sheetId, withheld, list, refetch]);

  const openCapture = async (fixedClass: string | null, title: string) => {
    if (presentation.current.withheld) return;
    captureOrigin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const version = presentation.current.version;
    setEnsureError(null);
    let id = sheetId;
    if (id === null) {
      // MATERIALIZE-ON-ATTACH: the one draft writer creates the sheet so
      // the paper-first visit exists before its first page lands.
      setEnsureBusy(true);
      try { id = await props.ensureSheet(); } catch { id = null; }
      if (!mounted.current) return;
      setEnsureBusy(false);
      if (presentation.current.withheld || version !== presentation.current.version) return;
      if (id === null) {
        setEnsureError(
          'The visit record could not be created, so nothing can attach yet. Try again.',
        );
        return;
      }
    }
    setDialog({ kind: 'capture', caseSheetId: id, fixedClass, title });
  };

  const captureOwner = useRef(openCapture);
  captureOwner.current = openCapture;
  useEffect(() => {
    props.onPaperCapture?.(withheld ? null : {
      open: () => { void captureOwner.current('case_sheet_page', 'Attach pages'); },
      busy: ensureBusy, error: ensureError,
    });
    return () => props.onPaperCapture?.(null);
  }, [props.onPaperCapture, withheld, ensureBusy, ensureError]);

  const ready = list.kind === 'ready' ? list.list : null;
  const pages =
    ready !== null ? activeItemsOfClass(ready.items, 'case_sheet_page') : [];
  const otherActive =
    ready !== null
      ? ready.items.filter(
          (i) => i.status === 'active' && i.evidenceClass !== 'case_sheet_page',
        )
      : [];
  const demoted = ready !== null ? voidedItems(ready.items) : [];

  const v14g = props.presentation === 'v14g';
  const directConfig = ready?.config ?? props.captureConfig ?? null;
  const directCaptured = async (photo: CapturedEvidencePhoto, kind: 'xray' | 'photo') => {
    const selection = selectedCapture.current;
    if (!selection) return;
    const { version } = selection;
    setEnsureError(null); setEnsureBusy(true);
    try {
      const target = await resolveDirectEvidenceTarget(selection.target, () => mounted.current && !presentation.current.withheld && version === presentation.current.version);
      const { bearer } = target;
      const id = sheetId ?? await props.ensureSheet();
      const currentBearer = await authBearer();
      if (!mounted.current) return;
      if (id === null || presentation.current.withheld || version !== presentation.current.version || currentBearer !== bearer) throw Error('The visit could not be prepared. Nothing was attached.');
      setDialog({ kind: 'capture', caseSheetId: id, fixedClass: kind === 'xray' ? 'xray' : null,
        title: kind === 'xray' ? 'RVG / X-ray' : 'Photo, scan or document',
        initialCapture: { photo, toothRefs: target.toothRefs, bearer }, initialClass: directConfig?.kinds.includes('intraoral_photo') ? 'intraoral_photo' : directConfig?.kinds[0], photoTiming: kind === 'photo' });
    } catch (error) { if (mounted.current) setEnsureError(error instanceof Error ? error.message : 'The upload could not be prepared.'); }
    finally { if (mounted.current) setEnsureBusy(false); }
  };
  const directControls = v14g ? <>
    {directConfig ? (['xray', 'photo'] as const).map(kind => <EvidenceCaptureControl key={kind}
      subject={kind === 'xray' ? 'xray' : 'intraoral_photo'}
      label={kind === 'xray' ? 'Upload RVG / X-ray' : 'Upload photo, scan or document'} variant="secondary"
      busy={ensureBusy || readingCapture || withheld || (kind === 'xray' && !directConfig.kinds.includes('xray')) || !directConfig.allowedMime.some(type => type.startsWith('image/') || (kind === 'photo' && type === 'application/pdf'))}
      captureSources={directConfig.captureSources}
      allowedTypes={directConfig.allowedMime.filter(type => type.startsWith('image/') || (kind === 'photo' && type === 'application/pdf'))}
      maxBytes={directConfig.maxBytes} renditionMaxBytes={directConfig.renditionMaxBytes}
      onFileSelected={() => { captureOrigin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; selectedCapture.current = { target: snapshotDirectEvidenceTarget(props.selectedToothRefs ?? []), version: presentation.current.version }; }} onReadStateChange={setReadingCapture}
      onCaptured={photo => { void directCaptured(photo, kind); }} onError={setEnsureError}
      testId={kind === 'xray' ? 'evidence-upload-xray' : 'evidence-upload-photo'} />) : <p role="status">Upload settings are unavailable. Reload this visit to try again.</p>}
    {directConfig && !directConfig.kinds.includes('xray') ? <p role="status">X-ray capture is not enabled for this organization.</p> : null}
    {directConfig && !directConfig.allowedMime.some(type => type.startsWith('image/')) ? <p role="status">Image uploads are not enabled for this organization.</p> : null}
  </> : null;
  const actions = (
        <div className="evidence-panel__actions">
          {!v14g ? <AcButton
            size="sm"
            variant="primary"
            disabled={ensureBusy}
            onClick={() => void openCapture('case_sheet_page', 'Attach pages')}
            data-testid="evidence-attach-pages"
          >
            {ensureBusy ? 'Preparing...' : 'Attach case-sheet pages'}
          </AcButton> : null}
          {directControls}
          {!v14g ? <AcButton
            size="sm"
            variant="secondary"
            disabled={ensureBusy}
            onClick={() => void openCapture(null, 'Add photo')}
            data-testid="evidence-add-photo"
          >
            Add photo or X-ray
          </AcButton> : null}
        </div>
  );
  return (
    <SectionShell
      embedded={v14g ? false : props.embedded}
      eyebrow={v14g ? undefined : "Paper first"}
      title={v14g ? "X-rays, photos & case-sheet scan" : "Case-sheet pages and evidence"}
      sub={v14g ? undefined : "Photograph the paper case sheet and clinical photos - typing stays optional. Tap a photo for full quality."}
      actions={v14g ? actions : undefined}
      testId="consult-evidence-panel"
    >
      <div className="evidence-panel">
        {ready !== null && (!v14g || ready.owedKinds.length > 0) ? (
          <p className="evidence-panel__owed" data-testid="evidence-owed-line">
            {owedKindsSummary(ready.owedKinds)}
          </p>
        ) : null}

        {!v14g ? actions : null}
        {ensureError !== null ? (
          <p className="evidence-dialog__error" role="alert">
            {ensureError}
          </p>
        ) : null}

        {sheetId === null ? (
          <EmptyState
            title="No evidence yet."
            body="Attach the paper case-sheet pages - the visit record is created with the first page."
            testId="evidence-empty-nosheet"
          />
        ) : list.kind === 'loading' ? (
          <LoadingState label="Loading evidence..." lines={2} />
        ) : list.kind === 'error' ? (
          <ErrorState
            title="Evidence could not be loaded."
            detail={list.message}
            action={
              <AcButton size="sm" variant="secondary" onClick={refetch}>
                Try again
              </AcButton>
            }
          />
        ) : (
          <>
            {pages.length > 0 ? (
              <div className="evidence-panel__group">
                <span className="evidence-panel__group-label">
                  Case-sheet pages ({pages.length})
                </span>
                <div className="evidence-strip" data-testid="evidence-page-strip">
                  {pages.map((item) => (
                    <div className="evidence-strip__cell" key={item.id}>
                      <EvidenceThumb
                        item={item}
                        clinicId={props.clinicId}
                        onOpenFull={(it) => setDialog({ kind: 'full', item: it })}
                      />
                      <button
                        type="button"
                        className="evidence-strip__void"
                        onClick={() => setDialog({ kind: 'void', item })}
                        data-testid={`evidence-void-${item.id}`}
                      >
                        Void
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {otherActive.length > 0 ? (
              <div className="evidence-panel__group">
                <span className="evidence-panel__group-label">
                  Photos, X-rays and consent ({otherActive.length})
                </span>
                <div className="evidence-strip" data-testid="evidence-gallery">
                  {otherActive.map((item) => (
                    <div className="evidence-strip__cell" key={item.id}>
                      <span className="evidence-strip__kind">
                        {evidenceClassLabel(item.evidenceClass)}
                      </span>
                      <EvidenceThumb
                        item={item}
                        clinicId={props.clinicId}
                        onOpenFull={(it) => setDialog({ kind: 'full', item: it })}
                      />
                      <button
                        type="button"
                        className="evidence-strip__void"
                        onClick={() => setDialog({ kind: 'void', item })}
                        data-testid={`evidence-void-${item.id}`}
                      >
                        Void
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {pages.length === 0 && otherActive.length === 0 ? (v14g ? <p className="consult-v14g__muted" data-testid="evidence-empty">Nothing attached yet.</p> : <EmptyState
                title="No evidence attached yet."
                body="Attach the paper case-sheet pages, or add a clinical photo."
                testId="evidence-empty"
              />
            ) : null}

            {demoted.length > 0 ? (
              <div className="evidence-panel__group">
                <span className="evidence-panel__group-label">Voided</span>
                <ul className="evidence-voided" data-testid="evidence-voided-list">
                  {demoted.map((item) => (
                    <li key={item.id} className="evidence-voided__row">
                      <AcBadge tone="pending">
                        {evidenceClassLabel(item.evidenceClass)}
                      </AcBadge>
                      <span className="evidence-voided__reason">
                        {evidenceVoidReasonLabel(item.voidReason ?? '')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>

      {dialog.kind === 'capture' ? (
        <EvidenceCaptureDialog
          key={dialog.caseSheetId}
          title={dialog.title}
          patientLabel={props.patientLabel}
          returnFocus={captureOrigin}
          initialCapture={dialog.initialCapture}
          initialClass={dialog.initialClass}
          photoTiming={dialog.photoTiming}
          caseSheetId={dialog.caseSheetId}
          fixedClass={dialog.fixedClass}
          // Org config from the list read when loaded; otherwise the dialog
          // resolves it itself (a just-materialized sheet) -- org config
          // always wins, nothing hard-codes the vocabulary.
          {...(ready !== null ? { config: ready.config } : {})}
          onAttached={async item => {
            const fresh = await refetch();
            if (dialog.initialCapture && (!item || !fresh?.items.some(row => row.id === item.id && row.caseSheetId === dialog.caseSheetId && row.clinicId === props.clinicId && row.sha256 === item.sha256 && JSON.stringify(row.toothRefs) === JSON.stringify(item.toothRefs) && row.photoPhase === item.photoPhase))) throw Error('The saved upload could not be checked. Retry the same capture.');
            if (dialog.initialCapture) setDialog({ kind: 'none' });
          }}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}
      {dialog.kind === 'full' ? (
        <EvidenceFullView
          key={`${props.clinicId}:${dialog.item.id}`}
          item={dialog.item}
          clinicId={props.clinicId}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}
      {dialog.kind === 'void' ? (
        <EvidenceVoidDialog
          item={dialog.item}
          clinicId={props.clinicId}
          onVoided={refetch}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}
    </SectionShell>
  );
}
